import toast from 'react-hot-toast'
import { useMutation } from '@tanstack/react-query'
import PubSub from 'pubsub-js'

import { PubSubEvents } from '@shared/hooks/src/usePubSub'
import { queryClient } from '@shared/providers/src/QueryClientProvider'
import API from '@shared/services/src/API'
import { getErrorDetails, handleError, includesOneOfErrorMessages, QK } from '@shared/utils'

import { isOutOfOfficeError, outOfOfficeBlockedMessage } from '../Helper'

/**
 * Responsible for sending a message to a conversation
 *
 * We have two flows:
 * - If the conversation already exists, we send the message to it
 * - If the conversation doesn't exist yet, we send the message to user directly
 *   via different api endpoint that creates conversation
 *
 * @param conversationId - conversation id when exist
 * @param userId - if conversation doesn't exist, we need to create it by sending a message to user id
 * @param config - axios config
 */
export default function useSendMessage({ conversationId, userId }, config) {
  return useMutation({
    mutationFn: ({ message, fromUserId, replyTo }) => {
      if (conversationId) {
        return API.conversations.id(conversationId).messages.send(
          {
            message_text: message,
            from_user_id: fromUserId,
            replying_message_id: replyTo,
          },
          config
        )
      }
      return API.users.id(userId).sendMessage({ message_text: message }, config)
    },
    onSuccess: ({ conversation }) => {
      return PubSub.publish(PubSubEvents.ConversationMessageSent, { userId, conversationId: conversation.id })
    },
    onError: (e) => {
      if (includesOneOfErrorMessages(e, ['Cannot send messages to an archived conversation'])) {
        return toast.error('This conversation has been archived. No new messages can be sent.')
      }
      if (includesOneOfErrorMessages(e, ['mention'])) {
        return toast.error(getErrorDetails(e).join(', ') || 'Please mention at least one user before sending this message.')
      }
      if (isOutOfOfficeError(e)) {
        if (conversationId) queryClient.invalidateQueries({ queryKey: QK.conversations.id(conversationId).details })
        return toast.error(outOfOfficeBlockedMessage)
      }
      return catchError(e)
    },
  })
}

/**
 * Responsible for getting conversation id by userId
 */
export function useGetConversationId({ userId }, config) {
  return useMutation({
    mutationFn: () => API.users.id(userId).sendMessage(null, config),
    onError: (e) => {
      if (isOutOfOfficeError(e)) {
        return toast.error('This user is currently Out of Office and cannot receive internal messages.')
      }
      return catchError(e)
    },
  })
}

function catchError(e) {
  if (includesOneOfErrorMessages(e, ['does not has permissions'])) {
    return toast.error("You don't have permissions to send a message to this user.")
  }
  return handleError(e, { message: 'Unable to send message. Please try again later.' })
}
